import React, { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { toSectionStatus } from '@eluve/fhir-data';

import { TodoDetailDrawer } from './components/TodoDetailDrawer';
import { TodosCard } from './components/TodosCard';
import { useFhirPatientId, usePatientTodos } from './data/service-requests';
import { visibleTodos } from './todo-list';

/**
 * The Patient to-dos vertical slice for the new patient profile Overview tab
 * (ENG-5368): the home-program ServiceRequests written by Care Plan
 * activation, read-only until the adherence model lands (ENG-5382). The open
 * drawer lives in the URL (`?serviceRequest=<id>`) so detail views stay
 * deep-linkable.
 */
const DRAWER_PARAM = 'serviceRequest';

export const PatientTodosSection: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const [searchParams, setSearchParams] = useSearchParams();

  const fhirPatient = useFhirPatientId(patientId);
  const todos = usePatientTodos(fhirPatient.data ?? null);

  const visible = useMemo(() => visibleTodos(todos.data ?? []), [todos.data]);

  const openId = searchParams.get(DRAWER_PARAM);
  const openTodo = visible.find((todo) => todo.id === openId);

  const openDrawer = useCallback(
    (serviceRequestId: string) => {
      setSearchParams(
        (params) => {
          // One profile drawer at a time — drop the My-tasks card's param so
          // a shared/crafted URL can't stack both sheets.
          params.delete('task');
          params.set(DRAWER_PARAM, serviceRequestId);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeDrawer = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(DRAWER_PARAM);
        return params;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const status = toSectionStatus(
    {
      loading: fhirPatient.loading || todos.loading,
      data: todos.data,
      readError: fhirPatient.readError ?? todos.readError,
    },
    () => visible.length === 0,
  );

  return (
    <>
      <TodosCard
        todos={visible}
        status={status}
        paging={todos}
        onOpen={openDrawer}
      />
      <TodoDetailDrawer
        todo={openTodo}
        open={Boolean(openId && openTodo)}
        onClose={closeDrawer}
        onReviewed={todos.refetch}
      />
    </>
  );
};
